import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, Lock, Sparkles, ThermometerSun, Camera, BellRing } from "lucide-react";
import HexagonField from "@/components/HexagonField";
import { usePageTitle } from "@/hooks/usePageTitle";

const IMG = "https://images.pexels.com/photos/22307516/pexels-photo-22307516.jpeg";
const ACCENT = "https://images.pexels.com/photos/34195903/pexels-photo-34195903.jpeg";

const Card = ({ icon: Icon, title, copy }) => (
  <div className="glass rounded-2xl p-6 hover:border-white/25 transition-colors">
    <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
      <Icon className="w-4 h-4" />
    </div>
    <div className="mt-5 font-display text-lg">{title}</div>
    <p className="text-sm text-white/60 mt-2 leading-relaxed">{copy}</p>
  </div>
);

export default function HomeAutomation() {
  usePageTitle(
    "Home Automation",
    "Smart-home devices built in India: lights, switches, curtain motors, retrofit modules, biometric locks, and a gateway that ties it all together. Designed to install fast and just work."
  );
  return (
    <div>
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-40"
             style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 25%, rgba(10,11,16,0.4) 100%), url(${IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <HexagonField />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono">Prime · Home Automation</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold mt-5 leading-[1.02] tracking-tighter max-w-4xl">
            <span className="gradient-text">Luxury</span> with a touch of Magic.
          </h1>
          <p className="mt-7 text-lg text-white/70 max-w-2xl leading-relaxed">
            Luxury with a touch of magic. We turn homes and offices into spaces that respond to you —
            lighting, climate, security, curtains, and access, controlled by a touch or your voice.
            Effortless to live with, beautiful to look at.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/marketplace"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_30px_rgba(157,76,221,0.5)]">
              Shop the catalog <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5">
              Book a home consultation
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">What we cover</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter max-w-2xl">
            A home that <span className="text-accent">listens</span>, learns and stays out of your way.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Card icon={Lightbulb} title="Smart lighting + scenes" copy="Tunable-white, dimmable, scene-aware. From COB downlights to ambient panels, every fixture is WeAutomate-built." />
            <Card icon={Sparkles} title="Curtains + shading" copy="Whisper-quiet motors with safe-stop sensors. Wake to sunrise, sleep to a single tap." />
            <Card icon={Lock} title="Locks + access" copy="Fingerprint, face, RFID, PIN, key. Glass-door, biometric, ZigBee or WiFi — your call." />
            <Card icon={ThermometerSun} title="Climate + HVAC" copy="Room-aware thermostats, AC linkup, fan zoning. Comfort, lower bill." />
            <Card icon={Camera} title="Cameras + doorbells" copy="2K AI cameras, video doorbells, 360 indoor pan/tilt. Person, parcel, pet recognition." />
            <Card icon={BellRing} title="Sensors + safety" copy="Motion, lux, door, leak, smoke, siren. Wired into your routines, not your worries." />
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden glass">
            <img src={ACCENT} alt="Smart home ambience" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Why WeAutomate</div>
            <h3 className="font-display text-3xl lg:text-4xl mt-3 tracking-tighter">
              One brand, end-to-end.
            </h3>
            <ul className="mt-7 space-y-4 text-white/70">
              <li className="flex gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-accent shrink-0" /><span>Every device is WeAutomate — no third-party stickers in your beautiful home.</span></li>
              <li className="flex gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-accent shrink-0" /><span>Local-first scene engine — your home keeps working when the cloud doesn't.</span></li>
              <li className="flex gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-accent shrink-0" /><span>Retrofit-friendly — most homes don't need rewiring.</span></li>
              <li className="flex gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-accent shrink-0" /><span>India-grade support — technicians come to you.</span></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
